import { NbiDimensions } from "../../common/generic/generic-nbi-dimensions";
import { NBIKpis } from "../../common/generic/generic-nbi-domains";
import { IGenericAggregateResponse } from "../../common/generic/generic-types";
import colors from "../../common/utils/colors";
import { Format } from "../../common/utils/format";

const monthList = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
];

export interface ICumulativeData {
  Mtd: any[];
  Mtd_Y1: any[];
  Mtd_Y2: any[];
  Symbol: string;
  Y1: number;
  Y2: number;
  Y3: number;
}

// tslint:disable-next-line:cognitive-complexity
export function MapData(rawdata: IGenericAggregateResponse) {
  const dimentions = new NbiDimensions();
  const kpis = new NBIKpis();
  const monthData: ICumulativeData = {
    Mtd: [],
    Mtd_Y1: [],
    Mtd_Y2: [],
    Symbol: "K",
    Y1: 0,
    Y2: 0,
    Y3: 0,
  };
  let ytdtotal = 0;
  let ytd1Total = 0;
  let ytd2Total = 0;

  monthData.Y1 = Format.GetYear(rawdata.request ? rawdata.request.date : "");
  monthData.Y2 = monthData.Y1 - 1;
  monthData.Y3 = monthData.Y1 - 2;

  const million = 1000000;
  const billion = 1000000000;
  let rowCount = 0;
  for (let index = 1; index <= monthList.length; index++) {
    if (
      rawdata.aggregates &&
      rawdata.aggregates[rowCount] &&
      index ===
        Number(
          rawdata.aggregates[rowCount].dimensionAttributeValues[
            dimentions.PERIOD_MONTH_ID
          ]
        )
    ) {
      const ytd = rawdata.aggregates[rowCount].indicatorKpiValues[kpis.NBI_YTD];
      const ytdPrevious =
        rawdata.aggregates[rowCount].indicatorKpiValues[kpis.NBI_FY_1];
      const ytdPrevious2 =
        rawdata.aggregates[rowCount].indicatorKpiValues[kpis.NBI_FY_2];

      if (!isNaN(ytd)) {
        ytdtotal += ytd;
      }

      if (!isNaN(ytdPrevious)) {
        ytd1Total += ytdPrevious;
      }
      if (!isNaN(ytdPrevious2)) {
        ytd2Total += ytdPrevious2;
      }

      if (
        monthData.Symbol === "K" &&
        (ytdtotal > million ||
          ytd1Total > million ||
          ytd2Total > million)
      ) {
        monthData.Symbol = "M";
      }

      if (
        monthData.Symbol === "M" &&
        (ytdtotal > billion ||
          ytd1Total > billion ||
          ytd2Total > billion)
      ) {
        monthData.Symbol = "B";
      }

      if (ytd === 0 || isNaN(ytd)) {
        monthData.Mtd.push(null);
      } else {
        monthData.Mtd.push({
          desc: `(${Format.FormatYoY(ytdtotal / ytd1Total - 1, true, true)}
             YoY)`,
          x: index - 1,
          y: ytdtotal,
        });
      }

      if (ytdPrevious === 0 || isNaN(ytdPrevious)) {
        monthData.Mtd_Y1.push(null);
      } else {
        monthData.Mtd_Y1.push({
          desc: `(${Format.FormatYoY(
            ytd1Total / ytd2Total - 1,
            true,
            true
          )} YoY)`,
          x: index - 1,
          y: ytd1Total,
        });
      }

      if (ytdPrevious2 === 0 || isNaN(ytdPrevious2)) {
        monthData.Mtd_Y2.push(null);
      } else {
        monthData.Mtd_Y2.push({
          x: index - 1,
          y: ytd2Total,
        });
      }
      rowCount++;
    } else {
      monthData.Mtd.push(null);
      monthData.Mtd_Y1.push(null);
      monthData.Mtd_Y2.push(null);
    }
  }

  if (
    ytdtotal === 0 &&
    ytd1Total === 0 &&
    ytd2Total === 0
  ) {
    return undefined;
  }

  ConvertData(monthData);
  return monthData;
}

export function BuildCumulativeOptions(data: ICumulativeData) {
  return {
    chart: {
      spacing: [5, 5, 10, 5],
    },
    colors,
    credits: {
      enabled: false,
    },
    lang: {
      decimalPoint: ".",
      thousandsSep: ",",
    },
    legend: {
      itemStyle: {
        fontSize: "9px",
        fontWeight: "normal",
        textOverflow: "ellipsis",
      },
      margin: 5,
      padding: 1,
    },
    series: GetSeriesWithBudget(data),
    title: {
      text: "",
    },
    tooltip: {
      crosshairs: true,
      pointFormat:
        '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:,.2f} ' +
        data.Symbol +
        "€</b> {point.desc}<br/>",
      shared: true,
    },
    xAxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      crosshairs: true,
    },
    yAxis: {
      labels: {
        format: "{value} " + data.Symbol,
      },
      title: {
        text: "NBI (" + data.Symbol + "€)",
      },
    },
  };
}

function ConvertData(data: ICumulativeData) {
  ConvertAsPerUnit(data.Mtd_Y2, data.Symbol);
  ConvertAsPerUnit(data.Mtd_Y1, data.Symbol);
  ConvertAsPerUnit(data.Mtd, data.Symbol);
}

export function ConvertAsPerUnit(data: any, unit: any) {
  data.forEach((element: any) => {
    if (element !== null) {
      switch (unit) {
        case "M": {
          element.y = Format.FormatNumber(element.y, 1000000, 2);
          break;
        }
        case "B": {
          element.y = Format.FormatNumber(element.y, 1000000000, 2);
          break;
        }
        default: {
          element.y = Format.FormatNumber(element.y, 1000, 2);
          break;
        }
      }
    }
  });
}

function ConvertBudgetAsPerUnit(data: any, unit: any) {
  data.forEach((elem: any) => {
    if (elem !== null) {
      switch (unit) {
        case "M": {
          elem.y = Format.FormatNumber(elem.y, 1000000, 2);
          break;
        }
        case "B": {
          elem.y = Format.FormatNumber(elem.y, 1000000000, 2);
          break;
        }
        default: {
          elem.y = Format.FormatNumber(elem.y, 1000, 2);
          break;
        }
      }
    }
  });
}

function GetSeriesWithBudget(data: ICumulativeData) {
  return [
    {
      color: "#B5CFFF",
      data: data.Mtd_Y2,
      marker: {
        symbol: "square",
      },
      name: "NBI " + data.Y3,
      type: "spline",
      zIndex: 0,
    },
    {
      color: "#b4bbcb",
      data: data.Mtd_Y1,
      marker: {
        symbol: "square",
      },
      name: "NBI " + data.Y2,
      type: "spline",
      zIndex: 1,
    },
    {
      color: "#444852",
      data: data.Mtd,
      marker: {
        symbol: "square",
      },
      name: "NBI " + data.Y1,
      type: "spline",
      zIndex: 2,
    },
  ];
}
